import { Pie, PieChart, Cell } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { AppConfig } from "@/utils/AppConfig";
import { useTranslation } from "react-i18next";

// Type for data
type PercentageData = {
  success: boolean;
  sales: number;
  onlineSales: number;
  cashSales: number;
  onlineRatio: number; // 0 to 1
  cashRatio: number;   // 0 to 1
};

type PercentageChartProps = {
  data: PercentageData;
};

type CustomizedLabelProps = {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  index: number;
  method: string;
};

export function PercentegeChart({ data }: PercentageChartProps) {
  const { t } = useTranslation();
  const label = (
    <span className="flex items-center gap-1">
      {t("PercentegeChart.Sales")} {AppConfig.currency.icon("white", 10)}
    </span>
  );

  const chartData = [
    {
      method: `${t("PercentegeChart.Online")}\n${data.onlineRatio}%`,
      value: data.onlineSales,
      fill: "#4f46e5", // Indigo
    },
    {
      method: `${t("PercentegeChart.Cash")}\n${data.cashRatio}%`,
      value: data.cashSales,
      fill: "#10b981", // Emerald
    },
  ];

  const chartConfig = {
    value: {
      label: label,
    },
    credit: {
      label: "Online",
      color: "#4f46e5",
    },
    cash: {
      label: "Cash",
      color: "#10b981",
    },
  } satisfies ChartConfig;

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    method
  }: CustomizedLabelProps) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const lines = method.split('\n');

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={14}
        className="leading-[1.8]" // Added line-height control
      >
        {lines.map((line, i) => (
          <tspan
            x={x}
            dy={i === 0 ? 0 : 20} // Increased from 15 to 20 for more space
            key={i}
            className="block" // Changed to block for better spacing
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <Card className="flex flex-col bg-white dark:shadow-none text-black dark:bg-white dark:text-black dark:border-0 p-2 w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto  rounded-lg">
      <CardHeader className="items-center">
        <CardTitle className="text-xl font-semibold text-gray-800 mb-10">
          {t("PercentegeChart.Main")}
        </CardTitle>
        <CardDescription className="text-sm sm:text-md text-center mt-1 sm:mt-2 space-y-1 sm:space-y-2">
          <span className="font-bold text-lg">{t("PercentegeChart.Title")}</span>
          <span className="flex items-center justify-center gap-1">

            <span className="w-4 h-4 sm:w-5 sm:h-5 inline-block rounded-md bg-green-500"></span>
            {t("PercentegeChart.Cash")} {data.cashSales} {AppConfig.currency.icon("black", 10)}
          </span>
          <span className="flex items-center justify-center gap-1">
            <span className="w-4 h-4 sm:w-5 sm:h-5 inline-block rounded-md bg-blue-500"></span>
            {t("PercentegeChart.Online")} {data.onlineSales} {AppConfig.currency.icon("black", 10)}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-2 sm:pb-4">
        <ChartContainer
          config={chartConfig}
          className="mx-auto aspect-square mt-[60px] h-auto w-full max-w-[280px] sm:max-w-[350px]"
        >
          <PieChart width={100} height={100}>
            <ChartTooltip
              content={<ChartTooltipContent nameKey="value" hideLabel />}
            />
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="method"
              strokeWidth={2}
              outerRadius="80%"
              label={renderCustomizedLabel}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}